import React from 'react';
import { Button, ButtonProps, styled, CircularProgress } from '@mui/material';

interface AudioButtonProps extends Omit<ButtonProps, 'variant'> {
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
  isLoading?: boolean;
  glowing?: boolean;
}

const StyledAudioButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isLoading' && prop !== 'glowing' && prop !== 'audioVariant',
})<AudioButtonProps & { audioVariant?: string }>(({ audioVariant, glowing }) => {
  const variants = {
    primary: {
      background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
      color: '#FFFFFF',
      '&:hover': {
        background: 'linear-gradient(135deg, #FF8A65 0%, #FFB74D 100%)',
        boxShadow: '0 8px 25px rgba(255, 107, 53, 0.4)',
      },
      '&:active': {
        background: 'linear-gradient(135deg, #E64A19 0%, #F57C00 100%)',
      },
    },
    secondary: {
      background: 'linear-gradient(135deg, #8E24AA 0%, #673AB7 100%)',
      color: '#FFFFFF',
      '&:hover': {
        background: 'linear-gradient(135deg, #BA68C8 0%, #9575CD 100%)',
        boxShadow: '0 8px 25px rgba(142, 36, 170, 0.4)',
      },
    },
    success: {
      background: 'linear-gradient(135deg, #00E676 0%, #00C853 100%)',
      color: '#000000',
      '&:hover': {
        background: 'linear-gradient(135deg, #69F0AE 0%, #00E676 100%)',
        boxShadow: '0 8px 25px rgba(0, 230, 118, 0.4)',
      },
    },
    warning: {
      background: 'linear-gradient(135deg, #FFB300 0%, #FF8F00 100%)',
      color: '#000000',
      '&:hover': {
        background: 'linear-gradient(135deg, #FFCC02 0%, #FFB300 100%)',
        boxShadow: '0 8px 25px rgba(255, 179, 0, 0.4)',
      },
    },
    error: {
      background: 'linear-gradient(135deg, #FF5252 0%, #D32F2F 100%)',
      color: '#FFFFFF',
      '&:hover': {
        background: 'linear-gradient(135deg, #FF8A80 0%, #FF5252 100%)',
        boxShadow: '0 8px 25px rgba(255, 82, 82, 0.4)',
      },
    },
  };

  const baseStyles = {
    borderRadius: 12,
    padding: '12px 24px',
    fontSize: '0.95rem',
    fontWeight: 600,
    textTransform: 'none' as const,
    border: 'none',
    position: 'relative' as const,
    overflow: 'hidden' as const,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
      transition: 'left 0.6s ease',
    },
    '&:hover::before': {
      left: '100%',
    },
    '&:hover': {
      transform: 'translateY(-2px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
    '&:focus-visible': {
      outline: '3px solid rgba(255, 107, 53, 0.5)',
      outlineOffset: 2,
      transform: 'translateY(-2px)',
    },
    '&:disabled': {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'rgba(255, 255, 255, 0.3)',
      transform: 'none',
      '&:hover': {
        transform: 'none',
        boxShadow: 'none',
      },
    },
    // High contrast mode support
    '@media (prefers-contrast: high)': {
      border: '2px solid currentColor',
    },
    // Reduced motion support
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
      '&::before': {
        transition: 'none',
      },
      '&:hover': {
        transform: 'none',
      },
      '&:active': {
        transform: 'none',
      },
      '&:focus-visible': {
        transform: 'none',
      },
    },
  };

  const glowingStyles = glowing ? {
    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
    '@keyframes pulse': {
      '0%, 100%': {
        boxShadow: '0 4px 20px rgba(255, 107, 53, 0.3)',
      },
      '50%': {
        boxShadow: '0 8px 30px rgba(255, 107, 53, 0.6)',
      },
    },
  } : {};

  return {
    ...baseStyles,
    ...variants[audioVariant as keyof typeof variants] || variants.primary,
    ...glowingStyles,
  };
});

export const AudioButton: React.FC<AudioButtonProps> = ({
  children,
  variant = 'primary',
  isLoading = false,
  disabled,
  glowing = false,
  ...props
}) => {
  return (
    <StyledAudioButton
      audioVariant={variant}
      disabled={disabled || isLoading}
      glowing={glowing}
      {...props}
    >
      {isLoading ? (
        <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
      ) : null}
      {children}
    </StyledAudioButton>
  );
};